// Тема інтерфейсу: 'system' (prefers-color-scheme) | 'light' | 'dark' → атрибут data-theme на <html>.
// Вибір зберігається в localStorage ('unisched-theme'); index.html застосовує його до першого рендеру.
import { useSyncExternalStore } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';

const KEY = 'unisched-theme';
const listeners = new Set<() => void>();

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

let current: ThemeChoice = typeof window === 'undefined' ? 'system' : read();

function apply(choice: ThemeChoice) {
  const el = document.documentElement;
  if (choice === 'system') el.removeAttribute('data-theme');
  else el.setAttribute('data-theme', choice);
}

export function getTheme(): ThemeChoice {
  return current;
}

export function setTheme(choice: ThemeChoice): void {
  current = choice;
  try {
    if (choice === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    /* приватний режим — лише в пам'яті */
  }
  apply(choice);
  for (const l of listeners) l();
}

/** Наступна тема в циклі перемикача: системна → світла → темна → системна. */
export function nextTheme(choice: ThemeChoice): ThemeChoice {
  return choice === 'system' ? 'light' : choice === 'light' ? 'dark' : 'system';
}

export const THEME_LABELS: Record<ThemeChoice, string> = {
  system: 'Тема: як у системі',
  light: 'Тема: світла',
  dark: 'Тема: темна',
};

export function useTheme(): [ThemeChoice, (t: ThemeChoice) => void] {
  const theme = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getTheme,
    () => 'system' as ThemeChoice,
  );
  return [theme, setTheme];
}
